/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {AnimateTimings, AnimationOptions, ɵStyleData} from '@angular/animations';

const /** @type {?} */ EMPTY_ANIMATION_OPTIONS: AnimationOptions = {};

export interface AstVisitor {
  visitTrigger(ast: TriggerAst, context: any): any;
  visitState(ast: StateAst, context: any): any;
  visitTransition(ast: TransitionAst, context: any): any;
  visitSequence(ast: SequenceAst, context: any): any;
  visitGroup(ast: GroupAst, context: any): any;
  visitAnimate(ast: AnimateAst, context: any): any;
  visitStyle(ast: StyleAst, context: any): any;
  visitKeyframes(ast: KeyframesAst, context: any): any;
  visitReference(ast: ReferenceAst, context: any): any;
  visitAnimateChild(ast: AnimateChildAst, context: any): any;
  visitAnimateRef(ast: AnimateRefAst, context: any): any;
  visitQuery(ast: QueryAst, context: any): any;
  visitStagger(ast: StaggerAst, context: any): any;
  visitTiming(ast: TimingAst, context: any): any;
}
/**
 * @abstract
 */
export abstract class Ast {
/**
 * @abstract
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visit(ast: AstVisitor, context: any) {}
public options: AnimationOptions = EMPTY_ANIMATION_OPTIONS;
/**
 * @return {?}
 */
get params(): {[name: string]: any}|null { return this.options['params'] || null; }
}

function Ast_tsickle_Closure_declarations() {
/** @type {?} */
Ast.prototype.options;
}

export class TriggerAst extends Ast {
public queryCount: number = 0;
public depCount: number = 0;
/**
 * @param {?} name
 * @param {?} states
 * @param {?} transitions
 */
constructor(public name: string,
public states: StateAst[],
public transitions: TransitionAst[]) {
    super();
  }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any): any { return visitor.visitTrigger(this, context); }
}

function TriggerAst_tsickle_Closure_declarations() {
/** @type {?} */
TriggerAst.prototype.queryCount;
/** @type {?} */
TriggerAst.prototype.depCount;
/** @type {?} */
TriggerAst.prototype.name;
/** @type {?} */
TriggerAst.prototype.states;
/** @type {?} */
TriggerAst.prototype.transitions;
}

export class StateAst extends Ast {
/**
 * @param {?} name
 * @param {?} style
 */
constructor(public name: string,
public style: StyleAst) { super(); }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any): any { return visitor.visitState(this, context); }
}

function StateAst_tsickle_Closure_declarations() {
/** @type {?} */
StateAst.prototype.name;
/** @type {?} */
StateAst.prototype.style;
}

export class TransitionAst extends Ast {
public queryCount: number = 0;
public depCount: number = 0;
/**
 * @param {?} matchers
 * @param {?} animation
 */
constructor(
public matchers: ((fromState: string, toState: string) => boolean)[],
public animation: Ast) {
    super();
  }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any): any { return visitor.visitTransition(this, context); }
}

function TransitionAst_tsickle_Closure_declarations() {
/** @type {?} */
TransitionAst.prototype.queryCount;
/** @type {?} */
TransitionAst.prototype.depCount;
/** @type {?} */
TransitionAst.prototype.matchers;
/** @type {?} */
TransitionAst.prototype.animation;
}

export class SequenceAst extends Ast {
/**
 * @param {?} steps
 */
constructor(public steps: Ast[]) { super(); }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any): any { return visitor.visitSequence(this, context); }
}

function SequenceAst_tsickle_Closure_declarations() {
/** @type {?} */
SequenceAst.prototype.steps;
}

export class GroupAst extends Ast {
/**
 * @param {?} steps
 */
constructor(public steps: Ast[]) { super(); }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any): any { return visitor.visitGroup(this, context); }
}

function GroupAst_tsickle_Closure_declarations() {
/** @type {?} */
GroupAst.prototype.steps;
}

export class AnimateAst extends Ast {
/**
 * @param {?} timings
 * @param {?} style
 */
constructor(public timings: TimingAst,
public style: StyleAst|KeyframesAst) { super(); }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any): any { return visitor.visitAnimate(this, context); }
}

function AnimateAst_tsickle_Closure_declarations() {
/** @type {?} */
AnimateAst.prototype.timings;
/** @type {?} */
AnimateAst.prototype.style;
}

export class StyleAst extends Ast {
public isEmptyStep = false;
/**
 * @param {?} styles
 * @param {?} easing
 * @param {?} offset
 */
constructor(
public styles: (ɵStyleData|string)[],
public easing: string|null,
public offset: number|null) {
    super();
  }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any): any { return visitor.visitStyle(this, context); }
}

function StyleAst_tsickle_Closure_declarations() {
/** @type {?} */
StyleAst.prototype.isEmptyStep;
/** @type {?} */
StyleAst.prototype.styles;
/** @type {?} */
StyleAst.prototype.easing;
/** @type {?} */
StyleAst.prototype.offset;
}

export class KeyframesAst extends Ast {
/**
 * @param {?} styles
 */
constructor(public styles: StyleAst[]) { super(); }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any): any { return visitor.visitKeyframes(this, context); }
}

function KeyframesAst_tsickle_Closure_declarations() {
/** @type {?} */
KeyframesAst.prototype.styles;
}

export class ReferenceAst extends Ast {
/**
 * @param {?} animation
 */
constructor(public animation: Ast) { super(); }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any): any { return visitor.visitReference(this, context); }
}

function ReferenceAst_tsickle_Closure_declarations() {
/** @type {?} */
ReferenceAst.prototype.animation;
}

export class AnimateChildAst extends Ast {
constructor() { super(); }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any): any { return visitor.visitAnimateChild(this, context); }
}
export class AnimateRefAst extends Ast {
/**
 * @param {?} animation
 */
constructor(public animation: ReferenceAst) { super(); }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any): any { return visitor.visitAnimateRef(this, context); }
}

function AnimateRefAst_tsickle_Closure_declarations() {
/** @type {?} */
AnimateRefAst.prototype.animation;
}

export class QueryAst extends Ast {
public originalSelector: string;
/**
 * @param {?} selector
 * @param {?} limit
 * @param {?} optional
 * @param {?} includeSelf
 * @param {?} animation
 */
constructor(
public selector: string,
public limit: number,
public optional: boolean,
public includeSelf: boolean,
public animation: Ast) {
    super();
  }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any): any { return visitor.visitQuery(this, context); }
}

function QueryAst_tsickle_Closure_declarations() {
/** @type {?} */
QueryAst.prototype.originalSelector;
/** @type {?} */
QueryAst.prototype.selector;
/** @type {?} */
QueryAst.prototype.limit;
/** @type {?} */
QueryAst.prototype.optional;
/** @type {?} */
QueryAst.prototype.includeSelf;
/** @type {?} */
QueryAst.prototype.animation;
}

export class StaggerAst extends Ast {
/**
 * @param {?} timings
 * @param {?} animation
 */
constructor(public timings: AnimateTimings,
public animation: Ast) { super(); }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any): any { return visitor.visitStagger(this, context); }
}

function StaggerAst_tsickle_Closure_declarations() {
/** @type {?} */
StaggerAst.prototype.timings;
/** @type {?} */
StaggerAst.prototype.animation;
}

export class TimingAst extends Ast {
/**
 * @param {?} duration
 * @param {?=} delay
 * @param {?=} easing
 */
constructor(
public duration: number,
public delay: number = 0,
public easing: string|null = null) {
    super();
  }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any): any { return visitor.visitTiming(this, context); }
}

function TimingAst_tsickle_Closure_declarations() {
/** @type {?} */
TimingAst.prototype.duration;
/** @type {?} */
TimingAst.prototype.delay;
/** @type {?} */
TimingAst.prototype.easing;
}

export class DynamicTimingAst extends TimingAst {
/**
 * @param {?} value
 */
constructor(public value: string) { super(0, 0, ''); }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any): any { return visitor.visitTiming(this, context); }
}

function DynamicTimingAst_tsickle_Closure_declarations() {
/** @type {?} */
DynamicTimingAst.prototype.value;
}

